import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { crmApi, extractCrmError } from "@/lib/crmApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { CarFront, Loader2, Plus, Search, UserRound, X } from "lucide-react";

export function normalizeClientName(client) {
  return client?.manual_name || client?.customer_name || client?.display_name || "Без имени";
}

export function formatPhone(value) {
  return value || "—";
}

export function formatVehicle(vehicle) {
  if (!vehicle) return "—";
  if (typeof vehicle === "string") return vehicle;
  const parts = [vehicle.brand, vehicle.model, vehicle.generation, vehicle.year]
    .map((v) => (v === null || v === undefined ? "" : String(v).trim()))
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

function getQuickClientDraft(searchQuery = "") {
  const term = String(searchQuery || "").trim();
  const digits = term.replace(/\D/g, "");
  const looksLikePhone = digits.length >= 6;
  return {
    display_name: looksLikePhone ? "" : term,
    phone: looksLikePhone ? term : "",
    car_model: "",
    manager_notes: "",
  };
}

function normalizePhoneDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizePhoneLast10(value) {
  const digits = normalizePhoneDigits(value);
  if (!digits) return "";
  return digits.slice(-10);
}

function prepareClientSearchQuery(value) {
  const term = String(value || "").trim();
  if (!term) return term;
  const digits = normalizePhoneDigits(term);
  if (digits.length >= 6 && /^[\d+\-() ]+$/.test(term)) {
    return term.replace(/\s/g, "");
  }
  return term;
}

export default function ClientSearch({
  token,
  selectedClient,
  onSelect,
  disabled,
  variant = "form",
  allowCreate = true,
}) {
  const compact = variant === "catalog";
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({});
  const [createDraft, setCreateDraft] = useState(() => getQuickClientDraft());

  useEffect(() => {
    if (selectedClient) {
      setQuery(normalizeClientName(selectedClient));
      setResults([]);
      setError(null);
      setCreateOpen(false);
      setCreateError(null);
      setCreateFieldErrors({});
      return;
    }
    setQuery("");
  }, [selectedClient]);

  useEffect(() => {
    if (selectedClient) return;
    const term = prepareClientSearchQuery(query);
    if (term.length < 2) {
      setResults([]);
      setError(null);
      setLoading(false);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await crmApi.listClients(token, {
          q: term,
          sort: "name_asc",
          page: 1,
          page_size: 8,
        });
        setResults(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        setError(extractCrmError(err, "Не удалось найти клиентов"));
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, token, selectedClient]);

  const clearSelection = () => {
    onSelect(null);
    setTouched(false);
    setResults([]);
    setQuery("");
    setCreateOpen(false);
    setCreateError(null);
    setCreateFieldErrors({});
    setCreateDraft(getQuickClientDraft());
  };

  const openCreateForm = () => {
    setCreateDraft(getQuickClientDraft(query));
    setCreateFieldErrors({});
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreateForm = () => {
    setCreateOpen(false);
    setCreateError(null);
    setCreateFieldErrors({});
  };

  const patchCreateDraft = (patch) => {
    setCreateDraft((prev) => ({ ...prev, ...patch }));
  };

  const handleCreateClient = async () => {
    if (creatingClient || disabled) return;

    const nextFieldErrors = {};
    if (!String(createDraft.phone || "").trim()) {
      nextFieldErrors.phone = "Телефон обязателен";
    }

    setCreateFieldErrors(nextFieldErrors);
    setCreateError(null);
    if (Object.keys(nextFieldErrors).length) {
      return;
    }

    setCreatingClient(true);
    try {
      const created = await crmApi.createClient(token, {
        display_name: createDraft.display_name || null,
        phone: createDraft.phone,
        car_model: createDraft.car_model || null,
        manager_notes: createDraft.manager_notes || null,
      });
      onSelect(created);
      setTouched(false);
      setResults([]);
      setQuery(normalizeClientName(created));
      setCreateOpen(false);
      setCreateError(null);
      setCreateFieldErrors({});
      toast.success("Клиент создан и выбран для заказа");
    } catch (err) {
      const errorCode = err?.response?.data?.code;
      if (errorCode === "crm_duplicate_phone") {
        try {
          const data = await crmApi.listClients(token, {
            q: prepareClientSearchQuery(createDraft.phone),
            sort: "name_asc",
            page: 1,
            page_size: 8,
          });
          const phoneNeedle = normalizePhoneLast10(createDraft.phone);
          const existingClient = (Array.isArray(data?.items) ? data.items : []).find((client) => {
            const candidate = client?.phone || client?.phone_normalized || "";
            return normalizePhoneLast10(candidate) === phoneNeedle;
          });

          if (existingClient) {
            onSelect(existingClient);
            setTouched(false);
            setResults([]);
            setQuery(normalizeClientName(existingClient));
            setCreateOpen(false);
            setCreateError(null);
            setCreateFieldErrors({});
            toast.success("Клиент с таким телефоном уже есть в базе и выбран для заказа");
            return;
          }
        } catch (lookupErr) {
          setCreateError(extractCrmError(lookupErr, "Клиент с таким телефоном уже существует"));
          return;
        }

        setCreateError("Клиент с таким телефоном уже существует. Найдите его по телефону и выберите из списка.");
        setQuery(createDraft.phone || query);
        return;
      }

      setCreateError(extractCrmError(err, "Не удалось создать клиента"));
    } finally {
      setCreatingClient(false);
    }
  };

  if (selectedClient) {
    if (compact) {
      return (
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-700">Клиент</Label>
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
            <UserRound className="h-4 w-4 shrink-0 text-green-700" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-gray-900">
                {normalizeClientName(selectedClient)}
              </p>
              <p className="text-xs text-gray-500">
                {formatPhone(selectedClient.phone || selectedClient.phone_normalized)}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 shrink-0 p-0"
              onClick={clearSelection}
              disabled={disabled}
              title="Снять выбор клиента"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Клиент</p>
            <div className="mt-2 flex items-center gap-2">
              <UserRound className="h-4 w-4 text-slate-500" />
              <p className="text-base font-semibold text-slate-950">{normalizeClientName(selectedClient)}</p>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {formatPhone(selectedClient.phone || selectedClient.phone_normalized)}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              <CarFront className="mr-1 inline-block h-4 w-4 text-slate-400" />
              {formatVehicle(selectedClient.primary_vehicle || selectedClient.vehicles?.[0])}
            </p>
          </div>
          <Button type="button" variant="outline" onClick={clearSelection}>
            Изменить
          </Button>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-gray-700">Клиент</Label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => {
              setTouched(true);
              setQuery(e.target.value);
            }}
            disabled={disabled}
            placeholder="Поиск клиента: имя или телефон"
            className="h-8 pl-9 text-sm"
          />
          {loading && <p className="mt-1 text-xs text-gray-500">Ищем клиентов...</p>}
          {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
          {results.length > 0 && (
            <ul className="absolute z-30 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              {results.map((client) => (
                <li key={client.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                    onClick={() => {
                      onSelect(client);
                      setQuery(normalizeClientName(client));
                      setResults([]);
                    }}
                  >
                    <span className="font-medium text-gray-900">{normalizeClientName(client)}</span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {formatPhone(client.phone || client.phone_normalized)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {allowCreate && !loading && query.trim().length >= 2 && results.length === 0 && touched && !error && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openCreateForm}
              disabled={disabled || creatingClient}
              className="mt-2"
            >
              <Plus className="mr-2 h-4 w-4" />
              Создать клиента
            </Button>
          )}
        </div>
        {allowCreate && createOpen && (
          <CompactCreateForm
            createDraft={createDraft}
            createError={createError}
            createFieldErrors={createFieldErrors}
            creatingClient={creatingClient}
            disabled={disabled}
            onPatch={patchCreateDraft}
            onClose={closeCreateForm}
            onSubmit={handleCreateClient}
          />
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Клиент *</p>
      <div className="relative mt-2">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => {
            setTouched(true);
            setQuery(e.target.value);
          }}
          disabled={disabled}
          placeholder="Поиск по имени или телефону"
          className="h-11 pl-9"
        />
      </div>
      {allowCreate && (
        <p className="mt-2 text-xs text-slate-500">
          Если клиента нет в базе, можно быстро создать его прямо здесь и продолжить оформление заказа.
        </p>
      )}

      {loading && (
        <div className="mt-3 space-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-14 rounded-xl" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {!loading && query.trim().length >= 2 && results.length === 0 && touched && !error && allowCreate && (
        <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          <p>Клиент не найден.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openCreateForm}
            disabled={disabled || creatingClient}
            className="mt-3"
          >
            <Plus className="mr-2 h-4 w-4" />
            Создать клиента
          </Button>
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="mt-3 max-h-72 space-y-2 overflow-y-auto pr-1">
          {results.map((client) => (
            <button
              type="button"
              key={client.id}
              onClick={() => onSelect(client)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-left transition hover:border-[#3566f6] hover:bg-[#f5f8ff]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-950">{normalizeClientName(client)}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    {formatPhone(client.phone || client.phone_normalized)}
                  </p>
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p>#{client.id}</p>
                  <p className="mt-1">{client.messages_count || 0} сообщ.</p>
                </div>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                <CarFront className="mr-1 inline-block h-4 w-4 text-slate-400" />
                {formatVehicle(client.primary_vehicle)}
              </p>
            </button>
          ))}
        </div>
      )}

      {allowCreate && !createOpen && touched && query.trim().length >= 2 && (
        <div className="mt-3 flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={openCreateForm}
            disabled={disabled || creatingClient}
            className="text-slate-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Создать нового клиента
          </Button>
        </div>
      )}

      {allowCreate && createOpen && (
        <FormCreatePanel
          createDraft={createDraft}
          createError={createError}
          createFieldErrors={createFieldErrors}
          creatingClient={creatingClient}
          disabled={disabled}
          onPatch={patchCreateDraft}
          onClose={closeCreateForm}
          onSubmit={handleCreateClient}
        />
      )}
    </div>
  );
}

function CompactCreateForm({
  createDraft,
  createError,
  createFieldErrors,
  creatingClient,
  disabled,
  onPatch,
  onClose,
  onSubmit,
}) {
  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
      <p className="text-sm font-semibold text-gray-900">Новый клиент</p>
      {createError && <p className="mt-2 text-xs text-red-600">{createError}</p>}
      <div className="mt-3 grid gap-2">
        <Input
          value={createDraft.display_name}
          onChange={(e) => onPatch({ display_name: e.target.value })}
          disabled={disabled || creatingClient}
          placeholder="Имя"
          className="h-8 text-sm"
        />
        <Input
          value={createDraft.phone}
          onChange={(e) => onPatch({ phone: e.target.value })}
          disabled={disabled || creatingClient}
          placeholder="Телефон *"
          className="h-8 text-sm"
        />
        {createFieldErrors.phone && <p className="text-xs text-red-600">{createFieldErrors.phone}</p>}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={creatingClient}>
          Отмена
        </Button>
        <Button type="button" size="sm" onClick={onSubmit} disabled={disabled || creatingClient}>
          {creatingClient ? "Создаём..." : "Создать"}
        </Button>
      </div>
    </div>
  );
}

function FormCreatePanel({
  createDraft,
  createError,
  createFieldErrors,
  creatingClient,
  disabled,
  onPatch,
  onClose,
  onSubmit,
}) {
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950">Новый клиент</p>
          <p className="mt-1 text-xs text-slate-500">
            После создания клиент сразу подставится в заказ, а форма заказа останется открытой.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={creatingClient}>
          Скрыть
        </Button>
      </div>

      {createError && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {createError}
        </div>
      )}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div>
          <Label>Имя</Label>
          <Input
            value={createDraft.display_name}
            onChange={(e) => onPatch({ display_name: e.target.value })}
            disabled={disabled || creatingClient}
            className="mt-2"
            placeholder="Например, Иван"
          />
        </div>
        <div>
          <Label>Телефон *</Label>
          <Input
            value={createDraft.phone}
            onChange={(e) => onPatch({ phone: e.target.value })}
            disabled={disabled || creatingClient}
            className="mt-2"
            placeholder="+7 700 000 00 00"
            aria-invalid={!!createFieldErrors.phone}
          />
          {createFieldErrors.phone && (
            <p className="mt-1 text-xs text-red-600">{createFieldErrors.phone}</p>
          )}
        </div>
        <div>
          <Label>Автомобиль / модель</Label>
          <Input
            value={createDraft.car_model}
            onChange={(e) => onPatch({ car_model: e.target.value })}
            disabled={disabled || creatingClient}
            className="mt-2"
            placeholder="Например, Camry 55"
          />
        </div>
        <div className="md:col-span-2">
          <Label>Комментарий</Label>
          <Textarea
            rows={3}
            value={createDraft.manager_notes}
            onChange={(e) => onPatch({ manager_notes: e.target.value })}
            disabled={disabled || creatingClient}
            className="mt-2"
            placeholder="Короткая заметка для менеджера"
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={creatingClient}>
          Отмена
        </Button>
        <Button type="button" onClick={onSubmit} disabled={disabled || creatingClient}>
          {creatingClient ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Создаём...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Создать клиента
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
